/**
 * `<caos-access-denied-panel>` — the blocked state that states which grant is missing
 * without leaking what was hidden. (Catalog: Status & feedback › AccessDeniedPanel — the blocked state that states which grant is missing without leaking what was hidden.)
 *
 * A centred column: a neutral lock disc, a `heading` (defaulting to "You don't have
 * access"), an explanation (the default slot), an optional mono line naming the missing
 * `grant` (the permission the caller would need — never the content that was withheld),
 * and an `action` slot for the one way forward (a "Request access" control). Deliberately
 * neutral, not danger — a denied grant is a fact to state, not an error to alarm about —
 * so it reads the neutral surface/text tokens; the lock disc uses the info tint. The
 * container is a labelled `role="region"`. Styling seams: `::part(panel)`, `::part(icon)`,
 * `::part(title)`, `::part(message)`, `::part(grant)`, `::part(actions)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { makeIcon } from './icons.js';

let uid = 0;

const sheet = makeSheet(`
  :host { display: block; }

  .panel {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: var(--caos-space-3);
    padding: var(--caos-space-7) var(--caos-space-5);
    max-width: 440px;
    margin: 0 auto;
    background: var(--caos-color-surface);
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-lg);
  }

  .icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: var(--caos-radius-pill);
    background: var(--caos-color-info-bg);
    color: var(--caos-color-info);
  }
  .icon svg { display: block; width: 22px; height: 22px; }

  .title {
    font-family: var(--caos-font-display);
    font-weight: 600;
    font-size: 17px;
    line-height: 1.3;
    color: var(--caos-color-text);
  }
  .title:empty { display: none; }

  .message { font-size: 13px; line-height: 1.55; color: var(--caos-color-text-muted); }

  .grant {
    display: inline-flex;
    align-items: center;
    gap: var(--caos-space-2);
    font-family: var(--caos-font-mono);
    font-size: 12px;
    color: var(--caos-color-text);
    background: var(--caos-color-surface-2);
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    padding: var(--caos-space-1) var(--caos-space-3);
  }
  .grant:empty { display: none; }
  .grant .label {
    font-family: var(--caos-font);
    font-size: 11.5px;
    color: var(--caos-color-text-muted);
  }

  .actions { display: flex; gap: var(--caos-space-2); margin-top: var(--caos-space-2); }
  .actions:not(:has(*)) { display: none; }
`);

export class CaosAccessDeniedPanel extends CaosElement {
  static get observedAttributes(): string[] {
    return ['heading', 'grant'];
  }

  private titleEl: HTMLDivElement;
  private grantEl: HTMLSpanElement;
  private readonly seq = ++uid;

  constructor() {
    super(sheet);

    const panel = document.createElement('div');
    panel.className = 'panel';
    panel.setAttribute('part', 'panel');
    panel.setAttribute('role', 'region');

    const icon = document.createElement('span');
    icon.className = 'icon';
    icon.setAttribute('part', 'icon');
    icon.setAttribute('aria-hidden', 'true');
    icon.append(makeIcon('lock'));

    this.titleEl = document.createElement('div');
    this.titleEl.className = 'title';
    this.titleEl.setAttribute('part', 'title');
    this.titleEl.id = `caos-access-title-${this.seq}`;
    panel.setAttribute('aria-labelledby', this.titleEl.id);

    const message = document.createElement('div');
    message.className = 'message';
    message.setAttribute('part', 'message');
    message.append(document.createElement('slot'));

    this.grantEl = document.createElement('span');
    this.grantEl.className = 'grant';
    this.grantEl.setAttribute('part', 'grant');

    const actions = document.createElement('div');
    actions.className = 'actions';
    actions.setAttribute('part', 'actions');
    const actionSlot = document.createElement('slot');
    actionSlot.name = 'action';
    actions.append(actionSlot);

    panel.append(icon, this.titleEl, message, this.grantEl, actions);
    this.root.append(panel);
  }

  connectedCallback(): void {
    this.sync();
  }

  attributeChangedCallback(): void {
    this.sync();
  }

  private sync(): void {
    this.titleEl.textContent = this.getAttribute('heading') ?? "You don't have access";
    const grant = this.getAttribute('grant');
    this.grantEl.replaceChildren();
    if (grant) {
      const label = document.createElement('span');
      label.className = 'label';
      label.textContent = 'Requires';
      this.grantEl.append(label, document.createTextNode(grant));
    }
  }
}

define('caos-access-denied-panel', CaosAccessDeniedPanel);

declare global {
  interface HTMLElementTagNameMap {
    'caos-access-denied-panel': CaosAccessDeniedPanel;
  }
}
